import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Autocomplete,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import {
  Person as PersonIcon,
  Add as AddIcon,
  CalendarToday as CalendarIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import { useActiveMemberProfiles } from '../../hooks/useActiveMemberProfiles';
import { classAPI, memberProfileAPI, classInstanceAPI } from '../../services/api';
import { Class, MemberProfile, ClassInstance } from '../../types';
import { formatTimeString, formatDate } from '../../utils/dateUtils';

interface RegisterMemberDialogProps {
  open: boolean;
  onClose: () => void;
  classItem: Class | null;
  onSuccess: (message?: string) => void;
}

const RegisterMemberDialog: React.FC<RegisterMemberDialogProps> = ({
  open,
  onClose,
  classItem,
  onSuccess
}) => {
  // Fetch active member profiles
  const { profiles: memberProfiles, loading: loadingMembers, refetch: refetchMemberProfiles } = useActiveMemberProfiles({ autoLoad: false });
  
  const [selectedMember, setSelectedMember] = useState<MemberProfile | null>(null);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingInstances, setLoadingInstances] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // New states for recurring class handling
  const [classInstances, setClassInstances] = useState<ClassInstance[]>([]);
  const [selectedInstances, setSelectedInstances] = useState<string[]>([]);
  const [isRecurringClass, setIsRecurringClass] = useState(false);

  // Fetch member profiles and class instances when dialog opens
  useEffect(() => {
    if (open && classItem) {
      refetchMemberProfiles();
      setSelectedMember(null);
      setNotes('');
      setError(null);
      setSelectedInstances([]);
      
      // Check if this is a recurring class
      const recurring = !!(classItem.recurringPattern && classItem.recurringPattern.daysOfWeek && classItem.recurringPattern.daysOfWeek.length > 0);
      setIsRecurringClass(recurring);
      
      if (recurring) {
        fetchClassInstances();
      }
    }
  }, [open, classItem]);



  const fetchClassInstances = async () => {
    if (!classItem) return;
    
    try {
      setLoadingInstances(true);
      const endDate = new Date();
      endDate.setMonth(endDate.getMonth() + 1); // Get instances for next 1 month
      
      const response = await classInstanceAPI.getInstances({
        parentClassId: classItem.id,
        startDate: new Date().toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0],
        status: 'SCHEDULED',
        includeBookings: 'true'
      });
      
      setClassInstances(response.data || []);
    } catch (error: any) {
      console.error('Error fetching class instances:', error);
      setError('Failed to fetch class instances');
    } finally {
      setLoadingInstances(false);
    }
  };

  const handleRegister = async () => {
    if (!selectedMember || !classItem) return;
    
    if (isRecurringClass && selectedInstances.length === 0) {
      setError('Please select at least one class instance');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      if (isRecurringClass) {
        // Register for multiple instances
        const registrations: Array<{error?: string; instanceId?: string; message?: string}> = [];
        for (const instanceId of selectedInstances) {
          try {
            const response = await classInstanceAPI.registerMemberToInstance(instanceId, {
              memberProfileId: selectedMember.id,
              notes
            });
            console.log(response.data);
            registrations.push(response.data);
          } catch (error: any) {
            console.error(`Error registering member to instance ${instanceId}:`, error);
            registrations.push({ 
              error: error.response?.data?.message || 'Registration failed',
              instanceId 
            });
          }
        }
        
        // Only close dialog if there are no errors, otherwise let user see the error
        const hasErrors = registrations.some(r => r.error);
        if (!hasErrors) {
          onClose();
        }
        
        setTimeout(() => {
          const successCount = registrations.filter(r => !r.error).length;
          const errorCount = registrations.filter(r => r.error).length;
          const errorMessages = registrations.filter(r => r.error).map(r => r.error).join(', ');
          
          if (errorCount === 0) {
            onSuccess(`Member successfully registered for ${successCount} class instance${successCount > 1 ? 's' : ''}!`);
          } else if (successCount > 0) {
            onSuccess(`Member registered for ${successCount} instance${successCount > 1 ? 's' : ''}, but ${errorCount} registration${errorCount > 1 ? 's' : ''} failed. Errors: ${errorMessages}`);
          } else {
            // Show error instead of success when all registrations fail
            setError(`All registrations failed: ${errorMessages}`);
            // Don't close dialog so user can see the error
            return;
          }
        }, 100);
      } else {
        // Register for single class (legacy)
        const response = await classAPI.registerMemberToClass(classItem.id, {
          memberProfileId: selectedMember.id,
          notes
        });

        onClose();
        
        setTimeout(() => {
          onSuccess(response.data.message || 'Member registered successfully!');
        }, 100);
      }
      
    } catch (error: any) {
      console.error('Error registering member:', error);
      const errorMessage = error.response?.data?.message || 'Failed to register member';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleInstanceSelection = (instanceId: string, selected: boolean) => {
    if (selected) {
      setSelectedInstances(prev => [...prev, instanceId]);
    } else {
      setSelectedInstances(prev => prev.filter(id => id !== instanceId));
    }
  };

  const handleSelectAllInstances = () => {
    if (selectedInstances.length === classInstances.length) {
      setSelectedInstances([]);
    } else {
      setSelectedInstances(classInstances.map(instance => instance.id));
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  if (!classItem) return null;

  return (
    <Dialog
      open={open}
      disableEscapeKeyDown={true}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AddIcon />
          <Box>
            <Typography variant="h6">
              Register Member to {isRecurringClass ? 'Recurring Class' : 'Class'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {classItem.name} {!isRecurringClass && `• ${new Date(classItem.scheduleDate).toLocaleDateString()}`}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Member Selection */}
          <FormControl fullWidth>
            <Autocomplete
              options={memberProfiles}
              value={selectedMember}
              onChange={(event, newValue) => setSelectedMember(newValue)}
              getOptionLabel={(option) => `${option.firstName} ${option.lastName} (${option.email})`}
              getOptionKey={(option) => option.id}
              loading={loadingMembers}
              renderOption={(props, option) => {
                const { key, ...optionProps } = props;
                return (
                  <Box component="li" key={key} {...optionProps}>
                    <PersonIcon sx={{ mr: 2, color: 'text.secondary' }} />
                    <Box>
                      <Typography variant="body1">
                        {option.firstName} {option.lastName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {option.email} • {option.membershipTier?.name || 'No Tier'}
                      </Typography>
                    </Box>
                  </Box>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Member"
                  placeholder="Search members..."
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingMembers ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              noOptionsText={loadingMembers ? "Loading members..." : "No members found"}
            />
          </FormControl>

          {/* Instance Selection for Recurring Classes */}
          {isRecurringClass && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" component="div">
                  Select Class Instances ({selectedInstances.length} selected)
                </Typography>
                <Button
                  size="small"
                  onClick={handleSelectAllInstances}
                  disabled={loadingInstances || classInstances.length === 0}
                >
                  {selectedInstances.length === classInstances.length ? 'Deselect All' : 'Select All'}
                </Button>
              </Box>
              
              {loadingInstances ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                  <CircularProgress />
                </Box>
              ) : classInstances.length === 0 ? (
                <Alert severity="info">
                  No upcoming class instances found for the next 3 months.
                </Alert>
              ) : (
                <List sx={{ maxHeight: 300, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  {classInstances.map((instance) => (
                    <ListItem key={instance.id} dense>
                      <ListItemIcon>
                        <Checkbox
                          checked={selectedInstances.includes(instance.id)}
                          onChange={(e) => handleInstanceSelection(instance.id, e.target.checked)}
                        />
                      </ListItemIcon>
                      <ListItemIcon>
                        <CalendarIcon sx={{ color: 'text.secondary' }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2">
                              {formatDate(instance.sessionDate)}
                            </Typography>
                            <ScheduleIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                              {formatTimeString(instance.startTime)} - {formatTimeString(instance.endTime)}
                            </Typography>
                            <Chip
                              size="small"
                              label={`${instance.enrolledCount || 0}/${instance.capacity}`}
                              color={
                                (instance.enrolledCount || 0) >= instance.capacity
                                  ? 'error'
                                  : (instance.enrolledCount || 0) / instance.capacity > 0.8
                                  ? 'warning'
                                  : 'success'
                              }
                              variant="outlined"
                            />
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          )}

          {/* Notes */}
          <TextField
            fullWidth
            label="Notes (Optional)"
            multiline
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add any notes about this registration..."
          />

          {/* Registration Summary */}
          {selectedMember && (
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Registration Summary:
              </Typography>
              <Typography variant="body2">
                <strong>Member:</strong> {selectedMember.firstName} {selectedMember.lastName}
              </Typography>
              <Typography variant="body2">
                <strong>Class:</strong> {classItem.name}
              </Typography>
              {isRecurringClass ? (
                <Typography variant="body2">
                  <strong>Instances:</strong> {selectedInstances.length} selected
                </Typography>
              ) : (
                <>
                  <Typography variant="body2">
                    <strong>Date:</strong> {formatDate(classItem.scheduleDate)}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Capacity:</strong> {classItem.currentBookings || 0}/{classItem.capacity}
                  </Typography>
                </>
              )}
              {classItem.oneTimeCost && (
                <Typography variant="body2">
                  <strong>Cost per instance:</strong> ${classItem.oneTimeCost} (payment will be marked as pending)
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleRegister}
          variant="contained"
          disabled={
            !selectedMember || 
            loading || 
            (isRecurringClass && selectedInstances.length === 0)
          }
          startIcon={loading ? <CircularProgress size={20} /> : <AddIcon />}
        >
          {loading 
            ? 'Registering...' 
            : isRecurringClass 
              ? `Register for ${selectedInstances.length} Instance${selectedInstances.length !== 1 ? 's' : ''}`
              : 'Register Member'
          }
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RegisterMemberDialog;
